import type { Moment } from 'moment'
import moment from 'moment'

import { nonNullable, sortBy, unique } from 'lib/array/arrayUtils'
import { isSameDay } from 'lib/datetime/dateUtils'
import memoize from 'lib/memoize/memoize'
import { DEFAULT_FLIGHT_IMAGE_ID } from 'tripPlanner/config'

type OrderKey = keyof App.Order

// This function checks if the order has more than one item type in the order, making it a Package.
export function orderHasMultiItemTypes(order: App.Order) {
  const keysWithMoreThanOneItem: Array<OrderKey> = []
  const keysToCheck: Array<OrderKey> = [
    'items',
    'bedbankItems',
    'flightItems',
    'tourItems',
    'experienceItems',
  ]

  keysToCheck.forEach((keysToCheck) => {
    const orderKeyToCheck = order[keysToCheck]
    if (Array.isArray(orderKeyToCheck) && orderKeyToCheck.length > 0) {
      keysWithMoreThanOneItem.push(keysToCheck)
    }
  })

  return keysWithMoreThanOneItem.length > 1
}

export const getUniqueOfferIdsFromOrder = memoize(
  (order: App.Order): Array<string> => {
    return unique([
      ...order.items.map((i) => i.offerId),
      ...order.tourItems.map((i) => i.tourId),
    ])
  },
)

export const getImageIdForOrder = (
  order: App.Order,
  tourV2Snapshot?: App.Tours.TourV2OfferSnapshot,
): string | undefined => {
  if (order.items.length > 0 && order.items[0]?.offer.image.id) {
    // Accommodation
    return order.items[0].offer.image.id
  }
  if (order.bedbankItems.length > 0 && order.bedbankItems[0].offer.image.id) {
    return order.bedbankItems[0].offer.image.id
  }
  if (order.tourItems.length > 0 && tourV2Snapshot?.imageId) {
    return tourV2Snapshot.imageId
  }
  if (order.flightItems.length > 0) {
    return DEFAULT_FLIGHT_IMAGE_ID
  }
}

export const extractOrderStartAndEndDate = (
  order: App.Order,
): { startDate?: Moment, endDate?: Moment } => {
  const dates: Array<Moment> = nonNullable([
    ...order.flightItems.map((i) => moment(i.arrivalDate)),
    ...order.flightItems.map((i) => moment(i.departureDate)),
    ...order.bedbankItems.map((i) => i.checkIn),
    ...order.bedbankItems.map((i) => i.checkOut),
    ...order.items.map((i) =>
      i.reservation ? moment(i.reservation.startDate) : undefined,
    ),
    ...order.items.map((i) =>
      i.reservation ? moment(i.reservation.endDate) : undefined,
    ),
    ...order.experienceItems.map((i) => (i.date ? moment(i.date) : undefined)),
    ...order.tourItems.map((i) => moment(i.tour.startDate)),
    ...order.tourItems.map((i) => moment(i.tour.endDate)),
  ])

  if (dates.length === 0) {
    return {}
  }

  const sorted = sortBy(dates, (date) => date.valueOf(), 'asc')

  const startDate = sorted[0]
  const endDate = sorted[sorted.length - 1]

  if (isSameDay(startDate.toDate(), endDate.toDate())) {
    return { startDate }
  }
  return { startDate, endDate }
}
